'use client';

import { ArrowDown, Loader2, Minus, Pencil, Plus, Trash2, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Drawer, DrawerClose, DrawerContent, DrawerTitle, DrawerTrigger } from '@/components/ui/drawer';
import { Input } from '@/components/ui/input';
import { getCartLineInfo } from '@/features/totems/domain/service/cart-line';
import { formatTotemPrice } from '@/features/totems/domain/service/totem-price';
import { useDiscountCode } from '@/features/totems/presentation/hooks/web-totem/use-discount-code';
import {
  useTotemActions,
  useTotemCartState,
  useTotemCatalog,
  useTotemOrder,
} from '@/features/totems/presentation/providers/web-totem/totem-provider';
import { cn } from '@/shared/utils/utils';

export function TotemOrderPanel() {
  return (
    <>
      {/* Desktop view */}
      <div className="hidden h-full lg:flex">
        <DesktopOrderPanelContent />
      </div>
      {/* Mobile view (Drawer) */}
      <div className="fixed bottom-0 left-0 z-50 flex w-full shadow-[0_-10px_30px_rgba(0,0,0,0.15)] lg:hidden">
        <MobileOrderDrawer />
      </div>
    </>
  );
}

function DesktopOrderPanelContent() {
  const { totem, menu } = useTotemCatalog();
  const { cart } = useTotemCartState();
  const { orderType } = useTotemOrder();
  const {
    setCheckoutOpen,
    setSelectedProduct,
    setEditingCartItemId,
    desktopCheckoutRef,
    setOrderOptionsOpen,
    setOrderType,
    setPickupMethod,
    setBeeperNumber,
  } = useTotemActions();

  const {
    discountCode,
    discountError,
    isPending,
    changeDiscountCode,
    applyDiscount: handleApplyDiscount,
  } = useDiscountCode({ locationId: totem?.location_id, cart });

  return (
    <aside className="z-10 flex h-full w-[350px] shrink-0 flex-col border-l border-border bg-background shadow-[-4px_0_15px_-3px_rgba(0,0,0,0.03)] dark:border-zinc-800 dark:bg-[#2C2C2E]">
      {/* Header */}
      <div
        ref={desktopCheckoutRef as React.RefObject<HTMLDivElement>}
        className="flex shrink-0 flex-col gap-5 border-b border-border px-6 py-6 dark:border-zinc-800"
      >
        <div className="flex w-full items-center rounded-xl bg-muted/40 p-1 dark:bg-zinc-800/40">
          <button
            type="button"
            onClick={() => {
              setOrderType('takeaway');
              setPickupMethod(null);
              setBeeperNumber('');
            }}
            className={cn(
              'flex-1 rounded-lg px-3 py-2 text-[13px] font-bold transition-all',
              orderType === 'takeaway' || !orderType
                ? 'bg-zinc-200 text-foreground shadow-sm dark:bg-[#4a4a4c]'
                : 'text-muted-foreground hover:text-foreground'
            )}
          >
            Para llevar
          </button>
          <button
            type="button"
            onClick={() => {
              setOrderType('eat_here');
              setOrderOptionsOpen(true);
            }}
            className={cn(
              'flex-1 rounded-lg px-3 py-2 text-[13px] font-bold transition-all',
              orderType === 'eat_here'
                ? 'bg-zinc-200 text-foreground shadow-sm dark:bg-[#4a4a4c]'
                : 'text-muted-foreground hover:text-foreground'
            )}
          >
            Comer aquí
          </button>
        </div>

        <div className="flex w-full items-center justify-between">
          <h2 className="text-2xl leading-none font-bold tracking-tight text-foreground dark:text-white">Tu pedido</h2>
          <div className="flex flex-col items-end">
            <span className="text-lg leading-none font-bold text-foreground dark:text-white">{cart.itemCount}</span>
            <span className="text-sm font-medium text-muted-foreground dark:text-zinc-400">productos</span>
          </div>
        </div>
      </div>

      {/* Discount Code Section */}
      <div className="border-b border-border bg-muted/20 px-6 py-4 dark:border-zinc-800 dark:bg-zinc-800/20">
        <div className="flex w-full items-center gap-2 rounded-xl border border-border bg-background p-1.5 dark:border-zinc-700 dark:bg-[#3a3a3a]">
          <Input
            placeholder="Código de descuento"
            className="h-9 border-none bg-transparent px-3 text-sm font-semibold text-foreground shadow-none placeholder:text-muted-foreground focus-visible:ring-0 dark:text-white dark:placeholder:text-zinc-500"
            value={discountCode}
            onChange={e => {
              changeDiscountCode(e.target.value);
            }}
            onKeyDown={e => {
              if (e.key === 'Enter') handleApplyDiscount();
            }}
            disabled={isPending || cart.discount !== null}
          />
          {cart.discount ? (
            <Button
              variant="ghost"
              size="sm"
              className="h-9 px-4 font-bold text-red-400 hover:bg-transparent hover:text-red-300"
              onClick={() => cart.clearDiscount()}
            >
              Quitar
            </Button>
          ) : (
            <Button
              size="sm"
              className="h-9 rounded-lg bg-[#dce3fe] px-4 font-bold text-[#2c2c2e] hover:bg-[#c9d3fd]"
              onClick={handleApplyDiscount}
              disabled={isPending || !discountCode.trim()}
            >
              {isPending ? <Loader2 className="size-4 animate-spin" /> : 'Aplicar'}
            </Button>
          )}
        </div>
        {discountError && <p className="mt-2 px-2 text-xs font-medium text-red-400">{discountError}</p>}
        {cart.discount && (
          <p className="mt-2 px-2 text-xs font-medium text-emerald-500 dark:text-emerald-400">
            Descuento aplicado: {cart.discount.code}
            {cart.discount.type === 'percentage'
              ? ` (${cart.discount.amount}%)`
              : ` (-${formatTotemPrice(cart.discount.amount)})`}
          </p>
        )}
      </div>

      {/* Content - Items List */}
      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto px-6 py-2">
        {cart.items.length === 0 ? (
          <div className="flex flex-1 flex-col items-center justify-center gap-4 text-center text-muted-foreground dark:text-zinc-500">
            <ShoppingCartIcon className="size-16 opacity-30" />
            <p className="text-lg font-medium">Tu pedido está vacío</p>
          </div>
        ) : (
          <ul className="flex flex-col">
            {cart.items.map(item => {
              const { lineTotal, product, isAtStockLimit, hasOptions } = getCartLineInfo(
                item,
                cart.items,
                menu?.products
              );

              return (
                <li key={item.id} className="flex flex-col gap-2 border-b border-border py-5 dark:border-zinc-800">
                  <div className="flex gap-4">
                    {/* Left Side: Avatar, Title, Price */}
                    <div className="flex min-w-0 flex-1 gap-3">
                      <Avatar className="size-14 shrink-0 rounded-xl border border-border bg-muted dark:border-zinc-700 dark:bg-zinc-800">
                        <AvatarImage alt={item.name} src={item.imageUrl} className="object-cover" />
                        <AvatarFallback className="rounded-xl bg-muted font-bold text-foreground dark:bg-zinc-800 dark:text-white">
                          {item.name.slice(0, 1)}
                        </AvatarFallback>
                      </Avatar>

                      <div className="flex min-w-0 flex-1 flex-col gap-0.5 pt-0.5">
                        <span className="truncate text-base leading-tight font-bold text-foreground dark:text-white">
                          {item.name}
                        </span>
                        <span className="text-base font-bold text-orange-500">{formatTotemPrice(lineTotal)}</span>
                      </div>
                    </div>

                    {/* Right Side: 2x2 Buttons Grid */}
                    <div className="flex shrink-0 flex-col items-end gap-2">
                      {/* Row 1: Qty [-] [1] [+] */}
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          className="flex size-9 shrink-0 items-center justify-center rounded-full border-none bg-red-500 text-white shadow-none transition-colors hover:bg-red-600"
                          onClick={() => {
                            if (item.quantity > 1) {
                              cart.updateQuantity(item.id, item.quantity - 1);
                            } else {
                              cart.removeItem(item.id);
                            }
                          }}
                        >
                          <Minus className="size-4 stroke-[3]" />
                        </button>
                        <div className="flex size-9 items-center justify-center rounded-xl border border-border bg-muted dark:border-zinc-700 dark:bg-[#3a3a3a]">
                          <span className="text-lg font-bold text-foreground dark:text-white">{item.quantity}</span>
                        </div>
                        <button
                          type="button"
                          disabled={isAtStockLimit}
                          className="flex size-9 shrink-0 items-center justify-center rounded-full border-none bg-emerald-500 text-white shadow-none transition-colors hover:bg-emerald-600 disabled:cursor-not-allowed disabled:opacity-40"
                          onClick={() => {
                            if (!isAtStockLimit) {
                              cart.updateQuantity(item.id, item.quantity + 1);
                            }
                          }}
                        >
                          <Plus className="size-4 stroke-[3]" />
                        </button>
                      </div>

                      {/* Row 2: Edit & Trash */}
                      <div className="flex items-center gap-2">
                        {hasOptions && (
                          <button
                            type="button"
                            className="flex size-9 shrink-0 items-center justify-center rounded-full border-none bg-orange-500 text-white shadow-none transition-colors hover:bg-orange-600"
                            onClick={() => {
                              if (product) {
                                setEditingCartItemId(item.id);
                                setSelectedProduct(product);
                              }
                            }}
                          >
                            <Pencil className="size-3.5" />
                          </button>
                        )}

                        <button
                          type="button"
                          className="flex size-9 shrink-0 items-center justify-center rounded-full border-none bg-red-500 text-white shadow-none transition-colors hover:bg-red-600"
                          onClick={() => cart.removeItem(item.id)}
                        >
                          <X className="size-4 stroke-[3]" />
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Subselections Full Width Row Below */}
                  {item.subselections.length > 0 && (
                    <div className="mt-1 ml-[68px] flex flex-col gap-1 border-l-2 border-emerald-500/20 pl-2.5">
                      {item.subselections.map(sub => (
                        <span
                          key={sub.subselectionItemId}
                          className="text-[13px] leading-snug font-medium break-words whitespace-normal text-emerald-600 dark:text-emerald-400"
                        >
                          {sub.quantity > 1 ? `${sub.quantity}x ` : ''}
                          {sub.name} {sub.price > 0 ? `(+${formatTotemPrice(sub.price)})` : ''}
                        </span>
                      ))}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* Footer */}
      <div className="flex shrink-0 flex-col gap-5 border-t border-border bg-background px-6 py-6 dark:border-zinc-800 dark:bg-[#2C2C2E]">
        <div className="flex items-center justify-between text-foreground dark:text-white">
          <span className="text-xl font-bold">Total</span>
          <div className="flex items-start gap-1 text-orange-500">
            <span className="mt-0.5 text-lg font-bold">$</span>
            <span className="text-3xl font-black tracking-tight">
              {new Intl.NumberFormat('es-AR', {
                minimumFractionDigits: 0,
                maximumFractionDigits: 2,
              }).format(Math.trunc(cart.total * 100) / 100)}
            </span>
          </div>
        </div>

        <div className="flex h-14 gap-3">
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                variant="outline"
                className="size-14 shrink-0 rounded-2xl border-red-200 bg-red-50 p-0 text-orange-500 shadow-none hover:border-red-300 hover:bg-red-100 hover:text-orange-600 dark:border-red-500/20 dark:bg-red-500/10 dark:hover:border-red-500/30 dark:hover:bg-red-500/20"
                disabled={cart.items.length === 0}
              >
                <Trash2 className="size-6" />
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>¿Vaciar el pedido?</AlertDialogTitle>
                <AlertDialogDescription>
                  Se eliminarán todos los productos de tu pedido actual. Esta acción no se puede deshacer.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Volver</AlertDialogCancel>
                <AlertDialogAction
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  onClick={() => {
                    cart.clearCart();
                    setOrderType(null);
                    setPickupMethod(null);
                    setBeeperNumber('');
                  }}
                >
                  Vaciar pedido
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          <Button
            className="h-full flex-1 rounded-2xl bg-[#3ba859] text-xl font-bold text-white shadow-lg transition-all hover:bg-[#34964e] hover:shadow-xl active:scale-[0.98]"
            disabled={cart.items.length === 0}
            onClick={() => setTimeout(() => setCheckoutOpen(true), 150)}
          >
            Cobrar
          </Button>
        </div>
      </div>
    </aside>
  );
}

function ShoppingCartIcon(props: React.ComponentProps<'svg'>) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
      {...props}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M2.25 3h1.386c.51 0 .955.343 1.087.835l.383 1.437M7.5 14.25a3 3 0 0 0-3 3h15.75m-12.75-3h11.218c1.121-2.3 2.1-4.684 2.924-7.138a60.114 60.114 0 0 0-16.536-1.84M7.5 14.25 5.106 5.272M6 20.25a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0Zm12.75 0a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0Z"
      />
    </svg>
  );
}

function MobileOrderDrawer() {
  const { totem, menu } = useTotemCatalog();
  const { cart } = useTotemCartState();
  const {
    setCheckoutOpen,
    setSelectedProduct,
    setEditingCartItemId,
    setOrderType,
    setPickupMethod,
    setBeeperNumber,
    mobileCheckoutRef,
  } = useTotemActions();

  const {
    discountCode,
    discountError,
    isPending,
    changeDiscountCode,
    applyDiscount: handleApplyDiscount,
  } = useDiscountCode({ locationId: totem?.location_id, cart });
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(min-width: 640px)');
    const handleResize = (e: MediaQueryListEvent | MediaQueryList) => {
      if (e.matches && open) {
        setOpen(false);
      }
    };
    handleResize(mediaQuery);
    mediaQuery.addEventListener('change', handleResize);
    return () => mediaQuery.removeEventListener('change', handleResize);
  }, [open]);

  const truncatedTotal = Math.trunc(cart.total * 100) / 100;
  const hasDecimals = truncatedTotal % 1 !== 0;
  const formattedTotal = new Intl.NumberFormat('es-AR', {
    minimumFractionDigits: hasDecimals ? 2 : 0,
    maximumFractionDigits: 2,
  }).format(truncatedTotal);

  return (
    <Drawer open={open} onOpenChange={setOpen}>
      <DrawerTrigger asChild>
        <button
          ref={mobileCheckoutRef as React.RefObject<HTMLButtonElement>}
          type="button"
          className="relative flex w-full flex-col items-end justify-end bg-[#353535] px-4 pt-6 pb-3 transition-transform active:scale-[0.98]"
        >
          {/* Fake rounded corners: sits inside the black bar so the black background is visible behind the curves */}
          <div className="absolute top-0 right-0 left-0 h-6 rounded-b-[32px] bg-[#e5e5e5]" />

          <div className="relative z-10 flex items-start gap-3 pt-2">
            <div className="flex flex-col items-start">
              <div className="flex h-11 min-w-[90px] items-center justify-start rounded-lg bg-[#c8d4fc] px-4">
                <span className="text-xl leading-none font-black text-[#2c2c2e]">${formattedTotal}</span>
              </div>
              <span className="mt-1 pl-4 text-[11px] font-bold text-white">Total</span>
            </div>

            <div className="flex h-11 items-center justify-center rounded-lg bg-[#4fd16d] px-6 text-base font-bold text-black shadow-sm">
              Pagar
            </div>
          </div>
        </button>
      </DrawerTrigger>

      <DrawerContent className="!mt-0 flex h-[100dvh] !max-h-none flex-col border-none bg-transparent p-0 text-white [&>div.mx-auto]:hidden">
        <DrawerTitle className="sr-only">Tu pedido</DrawerTitle>
        <div className="relative z-20 flex shrink-0 justify-end rounded-b-[24px] bg-[#e5e5e5] px-4 pt-2 pb-2">
          <DrawerClose asChild>
            <button
              type="button"
              className="flex items-center gap-2 rounded-lg bg-[#c8d4fc] px-4 py-2 font-bold text-black transition-opacity hover:opacity-90"
            >
              <span className="text-[15px]">Volver al inicio</span>
              <ArrowDown className="size-5" />
            </button>
          </DrawerClose>
        </div>

        <div className="relative z-10 -mt-6 flex min-h-0 flex-1 flex-col bg-[#353535] px-6">
          <div className="shrink-0 pt-10 pb-6">
            <label htmlFor="discount-code-mobile" className="mb-2 block text-sm font-medium text-[#e0e0e0]">
              Ingresá tu código de descuento
            </label>
            <div className="flex w-full items-center gap-2 rounded-xl border border-zinc-600 bg-[#2c2c2e] p-1.5">
              <Input
                id="discount-code-mobile"
                placeholder="CÓDIGO DE DESCUENTO"
                className="h-9 border-none bg-transparent px-3 text-sm font-semibold text-white uppercase shadow-none placeholder:text-zinc-500 focus-visible:ring-0"
                value={discountCode}
                onChange={e => {
                  changeDiscountCode(e.target.value);
                }}
                onKeyDown={e => {
                  if (e.key === 'Enter') handleApplyDiscount();
                }}
                disabled={isPending || cart.discount !== null}
              />
              {cart.discount ? (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-9 px-4 font-bold text-red-400 hover:bg-transparent hover:text-red-300"
                  onClick={() => cart.clearDiscount()}
                >
                  Quitar
                </Button>
              ) : (
                <Button
                  size="sm"
                  className="h-9 rounded-lg bg-[#c8d4fc] px-4 font-bold text-[#2c2c2e] hover:bg-[#b0c0f9]"
                  onClick={handleApplyDiscount}
                  disabled={isPending || !discountCode.trim()}
                >
                  {isPending ? <Loader2 className="size-4 animate-spin" /> : 'Aplicar'}
                </Button>
              )}
            </div>
            {discountError && <p className="mt-2 px-2 text-xs font-medium text-red-400">{discountError}</p>}
            {cart.discount && (
              <p className="mt-2 px-2 text-xs font-medium text-emerald-400">
                Descuento aplicado: {cart.discount.code}
                {cart.discount.type === 'percentage'
                  ? ` (${cart.discount.amount}%)`
                  : ` (-${formatTotemPrice(cart.discount.amount)})`}
              </p>
            )}
          </div>

          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-xl font-bold">Tu pedido</h2>
            <span className="text-sm text-zinc-400">
              {cart.itemCount} producto{cart.itemCount !== 1 && 's'}
            </span>
          </div>

          <div className="flex-1 overflow-y-auto pb-4">
            {cart.items.length === 0 ? (
              <div className="flex flex-col items-center justify-center gap-4 py-12 text-center text-zinc-500">
                <ShoppingCartIcon className="size-16 opacity-30" />
                <p className="text-lg font-medium">Tu pedido está vacío</p>
              </div>
            ) : (
              <ul className="flex flex-col gap-6">
                {cart.items.map(item => {
                  const { lineTotal, product, isAtStockLimit, hasOptions } = getCartLineInfo(
                    item,
                    cart.items,
                    menu?.products
                  );

                  return (
                    <li key={item.id} className="flex flex-col gap-2">
                      <div className="flex items-center gap-4">
                        <Avatar className="size-12 shrink-0 rounded-full bg-white">
                          <AvatarImage alt={item.name} src={item.imageUrl} className="object-contain p-1" />
                          <AvatarFallback className="rounded-full bg-white font-bold text-black">
                            {item.name.slice(0, 1)}
                          </AvatarFallback>
                        </Avatar>

                        <div className="flex min-w-0 flex-1 flex-col">
                          <span className="truncate text-base font-bold text-white">{item.name}</span>
                          {item.subselections.length > 0 && (
                            <span className="text-sm font-medium text-zinc-400">
                              {item.subselections.map(s => s.name).join(', ')}
                            </span>
                          )}
                          <span className="mt-0.5 text-base font-bold text-white">{formatTotemPrice(lineTotal)}</span>
                        </div>

                        <div className="flex shrink-0 flex-col items-end gap-2">
                          <div className="flex items-center gap-2">
                            {hasOptions && (
                              <button
                                type="button"
                                className="flex size-8 shrink-0 items-center justify-center rounded-full bg-[#ff9800] text-white"
                                onClick={() => {
                                  if (product) {
                                    setEditingCartItemId(item.id);
                                    setSelectedProduct(product);
                                    setOpen(false);
                                  }
                                }}
                              >
                                <Pencil className="size-4" />
                              </button>
                            )}
                            <button
                              type="button"
                              className="flex size-8 shrink-0 items-center justify-center rounded-full bg-[#ef5350] text-white"
                              onClick={() => cart.removeItem(item.id)}
                            >
                              <X className="size-4" />
                            </button>
                          </div>

                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              className="flex size-8 shrink-0 items-center justify-center rounded-full bg-[#ef5350] text-white"
                              onClick={() => {
                                if (item.quantity > 1) {
                                  cart.updateQuantity(item.id, item.quantity - 1);
                                } else {
                                  cart.removeItem(item.id);
                                }
                              }}
                            >
                              <Minus className="size-4" />
                            </button>
                            <div className="flex size-8 items-center justify-center rounded-xl border border-white/50 bg-transparent font-bold text-white">
                              {item.quantity}
                            </div>
                            <button
                              type="button"
                              disabled={isAtStockLimit}
                              className="flex size-8 shrink-0 items-center justify-center rounded-full bg-[#4caf50] text-white disabled:cursor-not-allowed disabled:opacity-40"
                              onClick={() => {
                                if (!isAtStockLimit) {
                                  cart.updateQuantity(item.id, item.quantity + 1);
                                }
                              }}
                            >
                              <Plus className="size-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>

        <div className="shrink-0 bg-[#353535] px-6 py-6 pb-8">
          <div className="mb-6 flex items-center gap-4">
            <span className="text-xl font-bold text-white">Total</span>
            <div className="h-[1px] flex-1 bg-zinc-600" />
            <span className="text-2xl font-black text-white">${formattedTotal}</span>
          </div>

          <div className="flex h-14 gap-4">
            <Button
              className="h-full flex-1 rounded-2xl bg-[#ff6b6b] text-lg font-bold text-white hover:bg-[#ff5252]"
              disabled={cart.items.length === 0}
              onClick={() => {
                cart.clearCart();
                setOrderType(null);
                setPickupMethod(null);
                setBeeperNumber('');
                setOpen(false);
              }}
            >
              <Trash2 className="mr-2 size-5" /> Cancelar
            </Button>
            <Button
              className="h-full flex-1 rounded-2xl bg-[#4fd16d] text-lg font-bold text-white hover:bg-[#45b960]"
              disabled={cart.items.length === 0}
              onClick={() => {
                setOpen(false);
                setTimeout(() => setCheckoutOpen(true), 150);
              }}
            >
              Pagar
            </Button>
          </div>
        </div>
      </DrawerContent>
    </Drawer>
  );
}
